"use client";

import { useRef, useState, useCallback, useEffect } from "react";
import { useAuth } from "@/components/auth-provider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordStrength } from "@/components/password-strength";
import { AliyunCaptcha, type AliyunCaptchaHandle, type CaptchaVerifyOutcome } from "@/components/aliyun-captcha";
import { trackAuth } from "@/lib/analytics";
import { getStoredAcquisition } from "@/lib/acquisition";
import { getInboxUrl } from "@/lib/email-providers";
import { AlertCircle, CheckCircle2, Inbox, Loader2, MailCheck, Send } from "lucide-react";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// 阿里云验证码 2.0 客户端配置（控制台：prefix=身份标识，SceneId=场景ID）
const CAPTCHA_PREFIX = process.env.NEXT_PUBLIC_ALIYUN_CAPTCHA_PREFIX;
const CAPTCHA_SCENE_ID = process.env.NEXT_PUBLIC_ALIYUN_CAPTCHA_SCENE_ID;
const CAPTCHA_CONFIGURED = !!(CAPTCHA_PREFIX && CAPTCHA_SCENE_ID);

interface AuthFormProps {
  mode?: "login" | "register";
  allowSwitch?: boolean;
  submitLabel?: string;
  onSuccess?: () => void;
}

interface AuthResult {
  token: string;
  user: { id: number; userID: string; username: string; email: string };
}

interface RegisterResponse {
  status?: string;
  email?: string;
  hint?: string;
  error?: string;
  captchaVerifyResult?: boolean;
}

/**
 * 登录/注册共享表单。
 * 注册态: 邮箱质量提示 + 密码强度 + 阿里云验证码 + 注册后"查收邮件"状态(含跳转收件箱)。
 * 阿里云验证码配置后由其 callback 驱动提交(按钮 type=button)；未配置时(dev)走普通提交，服务端放行。
 */
export function AuthForm({ mode: initialMode = "login", allowSwitch = true, submitLabel, onSuccess }: AuthFormProps) {
  const { login } = useAuth();
  const [mode, setMode] = useState<"login" | "register">(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [checkInbox, setCheckInbox] = useState<{ email: string; failed: boolean } | null>(null);
  const [notice, setNotice] = useState("");
  const captchaRef = useRef<AliyunCaptchaHandle>(null);

  const emailInvalid = mode === "register" && email.length > 0 && !EMAIL_RE.test(email);

  const showLoginError = useCallback((message: string) => {
    setError(message);
    if (/未激活|验证邮箱/.test(message) && email.includes("@")) {
      setNotice("账号还没有完成邮箱验证。可以检查收件箱，或使用下方入口重新发送验证邮件。");
    }
  }, [email]);

  /** 调注册接口。返回 {ok, data}，data.captchaVerifyResult 标识阿里云验证是否通过。 */
  const submitRegister = useCallback(async (captchaVerifyParam: string | null) => {
    const res = await fetch("/api/auth/register", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email,
        password,
        username,
        acquisition: getStoredAcquisition(),
        captchaVerifyParam,
      }),
    });
    const data = await res.json();
    let result: RegisterResponse = data;
    if (data.encrypted) {
      const { decrypt } = await import("@/lib/security/client-encrypt");
      result = decrypt(data.data);
    }
    return { ok: res.ok, data: result };
  }, [email, password, username]);

  /** 注册成功：进入"查收邮件"状态 */
  const handleRegisterSuccess = useCallback((payload: unknown) => {
    const r = payload as RegisterResponse;
    if (r?.status === "verify_email_failed") {
      setCheckInbox({ email: r.email || email, failed: true });
    } else {
      setCheckInbox({ email: r?.email || email, failed: false });
    }
    trackAuth("register");
  }, [email]);

  /** 阿里云验证码业务回调：调注册接口并回告验证结果 */
  const handleCaptchaVerify = useCallback(async (captchaVerifyParam: string): Promise<CaptchaVerifyOutcome> => {
    setError("");
    setSubmitting(true);
    try {
      const { ok, data } = await submitRegister(captchaVerifyParam);
      return {
        // captchaVerifyResult 仅在服务端复核失败时为 false；其余(校验/业务错误)视为验证码已通过
        captchaResult: data.captchaVerifyResult !== false,
        bizResult: ok,
        payload: data,
      };
    } catch {
      return { captchaResult: true, bizResult: false, payload: { error: "网络错误，请重试" } };
    } finally {
      setSubmitting(false);
    }
  }, [submitRegister]);

  /** 调登录接口，captchaVerifyParam 可为 null（dev 模式）。 */
  const submitLogin = useCallback(async (captchaVerifyParam: string | null) => {
    const res = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, captchaVerifyParam }),
    });
    const data = await res.json();
    let result: AuthResult | { error?: string; captchaVerifyResult?: boolean } = data;
    if (data.encrypted) {
      const { decrypt } = await import("@/lib/security/client-encrypt");
      result = decrypt(data.data);
    }
    return { ok: res.ok, data: result };
  }, [email, password]);

  /** 阿里云验证码业务回调：登录模式 */
  const handleLoginCaptchaVerify = useCallback(async (captchaVerifyParam: string): Promise<CaptchaVerifyOutcome> => {
    setError("");
    setSubmitting(true);
    try {
      const { ok, data } = await submitLogin(captchaVerifyParam);
      const d = data as { error?: string; captchaVerifyResult?: boolean; token?: string; user?: AuthResult["user"] };
      if (!ok && d.error) showLoginError(d.error);
      if (!ok) {
        return {
          captchaResult: d.captchaVerifyResult !== false,
          bizResult: false,
          payload: d,
        };
      }
      return { captchaResult: true, bizResult: true, payload: d };
    } catch {
      return { captchaResult: true, bizResult: false, payload: { error: "网络错误，请重试" } };
    } finally {
      setSubmitting(false);
    }
  }, [submitLogin, showLoginError]);

  const handleLoginBizSuccess = useCallback((payload: unknown) => {
    const d = payload as { token?: string; user?: AuthResult["user"] };
    if (d?.token && d?.user) {
      login(d.token, d.user);
      trackAuth("login");
      onSuccess?.();
    }
  }, [login, onSuccess]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setNotice("");

    // 验证码已配置：登录和注册均由 AliyunCaptcha 驱动
    if (CAPTCHA_CONFIGURED) {
      captchaRef.current?.trigger();
      return;
    }

    setSubmitting(true);

    try {
      if (mode === "login") {
        // dev 无验证码回退
        const { ok, data } = await submitLogin(null);
        const d = data as { error?: string; token?: string; user?: AuthResult["user"] };
        if (!ok) {
          showLoginError(d.error || "邮箱或密码错误");
          return;
        }
        handleLoginBizSuccess(d);
      } else {
        // dev 注册无验证码回退
        const { ok, data } = await submitRegister(null);
        if (!ok) {
          setError(data.error || "注册失败");
          return;
        }
        handleRegisterSuccess(data);
      }
      // CAPTCHA_CONFIGURED 时由 AliyunCaptcha 驱动，不走 form submit
    } catch {
      setError("网络错误，请重试");
    } finally {
      setSubmitting(false);
    }
  };

  // 查收邮件状态
  if (checkInbox) {
    return (
      <div className="space-y-4 py-1" data-clarity-mask="true">
        <div className={`mx-auto flex h-14 w-14 items-center justify-center rounded-full ${checkInbox.failed ? "bg-amber-50 text-amber-600" : "bg-emerald-50 text-emerald-600"}`}>
          {checkInbox.failed ? <AlertCircle className="h-7 w-7" /> : <MailCheck className="h-7 w-7" />}
        </div>
        <div className="space-y-1.5 text-center">
          <p className="font-semibold text-foreground">
            {checkInbox.failed ? "验证邮件未能发出" : "验证邮件已发送"}
          </p>
          <p className="text-sm leading-6 text-muted-foreground break-all">
            {checkInbox.failed
              ? checkInbox.email
              : <>已发送至 <span className="font-medium text-foreground">{checkInbox.email}</span>，请查收并点击验证链接激活账号</>}
          </p>
          {checkInbox.failed && (
            <p className="text-xs text-amber-600">邮件服务暂不可用，可稍后点重发或联系管理员</p>
          )}
        </div>
        <ResendButton email={checkInbox.email} onDone={() => setCheckInbox({ email: checkInbox.email, failed: false })} />
        {!checkInbox.failed && getInboxUrl(checkInbox.email) && (
          <a
            href={getInboxUrl(checkInbox.email)!}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex w-full items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium text-primary hover:bg-muted"
          >
            <Inbox className="h-4 w-4" />
            前往邮箱收件箱
          </a>
        )}
        <button
          type="button"
          onClick={() => { setCheckInbox(null); setMode("login"); }}
          className="block w-full text-sm text-muted-foreground hover:text-primary pt-1"
        >
          已有账号？去登录
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-clarity-mask="true">
      {mode === "register" && (
        <Input
          type="text"
          value={username}
          onChange={(e) => { setUsername(e.target.value); setError(""); setNotice(""); }}
          placeholder="用户名（2-32 位）"
          required
          minLength={2}
          maxLength={32}
          data-clarity-mask="true"
        />
      )}
      <div className="space-y-1.5">
        <Input
          type={mode === "login" ? "text" : "email"}
          value={email}
          onChange={(e) => { setEmail(e.target.value); setError(""); setNotice(""); }}
          placeholder={mode === "login" ? "邮箱或用户名" : "邮箱"}
          required
          aria-invalid={emailInvalid}
          autoComplete={mode === "login" ? "username" : "email"}
          className="h-10"
          data-clarity-mask="true"
        />
        {emailInvalid && (
          <p className="text-xs text-red-500 pl-1">邮箱格式不正确</p>
        )}
      </div>
      <div className="space-y-1.5">
        <Input
          type="password"
          value={password}
          onChange={(e) => { setPassword(e.target.value); setError(""); }}
          placeholder={mode === "login" ? "密码" : "密码（至少 8 位）"}
          required
          minLength={mode === "register" ? 8 : undefined}
          autoComplete={mode === "login" ? "current-password" : "new-password"}
          className="h-10"
          data-clarity-mask="true"
        />
        {mode === "register" && <PasswordStrength password={password} />}
      </div>
      {CAPTCHA_CONFIGURED && (
        <AliyunCaptcha
          ref={captchaRef}
          sceneId={CAPTCHA_SCENE_ID!}
          prefix={CAPTCHA_PREFIX!}
          onVerify={mode === "login" ? handleLoginCaptchaVerify : handleCaptchaVerify}
          onBizSuccess={mode === "login" ? handleLoginBizSuccess : handleRegisterSuccess}
          onBizError={(payload) => {
            console.log("[auth-form] onBizError payload:", payload);
            if (mode === "login") {
              showLoginError((payload as { error?: string })?.error || "登录失败");
            } else {
              setError((payload as RegisterResponse)?.error || "注册失败");
            }
          }}
        />
      )}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm leading-6 text-red-700">
          <div className="flex items-start gap-2">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
          {mode === "register" && allowSwitch && /该邮箱.*已被注册/.test(error) && (
            <button
              type="button"
              onClick={() => { setMode("login"); setError(""); }}
              className="text-primary underline hover:no-underline font-medium"
            >
              去登录
            </button>
          )}
        </div>
      )}
      {notice && mode === "login" && (
        <div className="rounded-lg border bg-muted/40 px-3 py-2 text-sm leading-6 text-muted-foreground">
          {notice}
          <ResendButton email={email} compact onDone={() => setNotice("验证邮件已重新发送，请查收邮箱。")} />
        </div>
      )}
      <Button
        type={CAPTCHA_CONFIGURED ? "button" : "submit"}
        onClick={CAPTCHA_CONFIGURED ? () => captchaRef.current?.trigger() : undefined}
        className="w-full h-10 text-sm"
        disabled={submitting}
      >
        {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
        {submitting ? "处理中..." : (submitLabel ?? (mode === "login" ? "登录" : "注册"))}
      </Button>
      {mode === "login" && (
        <div className="text-center">
          <a href="/auth/forgot" className="text-xs text-muted-foreground hover:text-primary hover:underline">
            忘记密码？
          </a>
        </div>
      )}
      {allowSwitch && (
        <div className="text-center text-sm text-muted-foreground">
          {mode === "login" ? (
            <button type="button" onClick={() => { setMode("register"); setError(""); }} className="text-primary hover:underline">
              没有账号？去注册
            </button>
          ) : (
            <button type="button" onClick={() => { setMode("login"); setError(""); }} className="text-primary hover:underline">
              已有账号？去登录
            </button>
          )}
        </div>
      )}
    </form>
  );
}

function ResendButton({ email, onDone, compact = false }: { email: string; onDone: () => void; compact?: boolean }) {
  const [countdown, setCountdown] = useState(60);
  const [sending, setSending] = useState(false);

  const tick = useCallback(() => setCountdown((c) => (c > 0 ? c - 1 : 0)), []);
  useEffect(() => {
    if (countdown <= 0) return;
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [countdown, tick]);

  const resend = async () => {
    if (countdown > 0 || sending || !email) return;
    setSending(true);
    try {
      await fetch("/api/auth/resend-verification", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      onDone();
      setCountdown(60);
    } finally {
      setSending(false);
    }
  };

  return (
    <Button
      type="button"
      variant="outline"
      className={compact ? "mt-2 h-8 w-full" : "w-full"}
      onClick={resend}
      disabled={countdown > 0 || sending || !email}
    >
      {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : countdown <= 0 ? <Send className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
      {countdown > 0 ? `重新发送 (${countdown}s)` : sending ? "发送中..." : "重新发送验证邮件"}
    </Button>
  );
}
